// components/AudioPlayer.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

function AudioPlayer() {
  const { bookId } = useParams();
  const [book, setBook] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  const audioRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const bookData = await api.getBook(bookId);
        setBook(bookData);
        
        const audioFilesData = await api.getAudioFiles(bookId);
        setAudioFiles(audioFilesData);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load audio book');
        setLoading(false);
      }
    };

    fetchData();
  }, [bookId]);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, currentTrack]);

  // Handle back button - go back to category or author
  const handleBack = () => {
    if (book && book.categoryId) {
      navigate(`/category/${book.categoryId}`);
    } else if (book && book.authorId) {
      navigate(`/author/${book.authorId}`);
    } else {
      navigate('/');
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handlePrevious = () => {
    if (currentTrack > 0) {
      setCurrentTrack(currentTrack - 1);
      setIsPlaying(true);
    }
  };

  const handleNext = () => {
    if (currentTrack < audioFiles.length - 1) {
      setCurrentTrack(currentTrack + 1);
      setIsPlaying(true);
    }
  };

  const handleTrackEnd = () => {
    if (currentTrack < audioFiles.length - 1) {
      setCurrentTrack(currentTrack + 1);
    } else {
      setIsPlaying(false);
    }
  };

  const handleTrackSelect = (index) => {
    setCurrentTrack(index);
    setIsPlaying(true);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const newTime = e.target.value;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Format time in minutes:seconds
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return "0:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) return <div className="loading">იტვირთება აუდიო წიგნები...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="audio-player">
      <div className="top-bar">
        <button onClick={handleBack} className="back-button">
          ← დაბრუნება
        </button>
      </div>
      
      <div className="book-details">
        <div className="book-image">
          <img src={`/images/books/${book?.id}.jpg`} alt={book?.name} onError={(e) => {e.target.src = '/images/default-book.jpg'}} />
        </div>
        <div className="book-info">
          <h2>{book?.name}</h2>
          <p className="author">By {book?.authorName || book?.author_name}</p>
        </div>
      </div>

      <div className="player-controls">
        <audio 
          ref={audioRef}
          src={audioFiles[currentTrack]?.url}
          onEnded={handleTrackEnd}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
        />
        
        <div className="seek-container">
          <span className="time-display">{formatTime(currentTime)}</span>
          <input 
            type="range"
            min="0"
            max={duration || 100}
            value={currentTime}
            onChange={handleSeek}
            className="seek-bar"
          />
          <span className="time-display">{formatTime(duration)}</span>
        </div>
        
        <div className="controls-main">
          <button onClick={handlePrevious} disabled={currentTrack === 0}>
            წინა
          </button>
          {isPlaying ? (
            <button onClick={handlePause}>შეჩერება</button>
          ) : (
            <button onClick={handlePlay}>დაკვრა</button>
          )}
          <button onClick={handleNext} disabled={currentTrack === audioFiles.length - 1}>
            შემდეგი
          </button>
        </div>
        
        <div className="now-playing">
          Playing: {audioFiles[currentTrack]?.filename}
        </div>
      </div>

      <div className="track-list">
        <h3>Tracks</h3>
        <ul>
          {audioFiles.map((file, index) => (
            <li 
              key={file.id} 
              className={index === currentTrack ? 'active' : ''}
              onClick={() => handleTrackSelect(index)}
            >
              {file.filename}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default AudioPlayer;